import makeApiCall from "./axiosIndex";

interface DashboardParams {
  orgIds?: string;
  siteIds?: string;
  headerIds?: string;
  lineIds?: string;
  modules?: string;
  startDate?: string;
  endDate?: string;
  budgetCategoryIds?: string;
  contractorIds?: string;
}

interface ReportParams extends DashboardParams {
  page?: number;
  pageSize?: number;
  // Drill-down specific params
  orderNumbers?: string;
  workOrderIds?: string;
  expenseReportIds?: string;
  prIds?: string;
  categoryIds?: string;
  workCategoryIds?: string;
  drillYear?: number;
  drillMonth?: number;
}

const DASHBOARD_BASE = "/budget-analysis-dashboard";
const REPORT_BASE = "/budget-analysis-report";

const budgetAnalysisDashboardApis = {
  // ==================== DASHBOARD APIs (15) ====================

  summaryReport: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/summary-report`, params }),

  budgetVsActualByCategory: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-vs-actual-by-category`, params }),

  budgetConsumptionBySource: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-consumption-by-source`, params }),

  budgetHealthIndicator: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-health-indicator`, params }),

  timeBudgetBurn: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/time-budget-burn`, params }),

  budgetDistributionByCategory: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-distribution-by-category`, params }),

  budgetDistributionByLineItem: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-distribution-by-line-item`, params }),

  budgetVsPurchaseOrders: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-vs-purchase-orders`, params }),

  budgetVsWorkOrders: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-vs-work-orders`, params }),

  budgetVsProjectTasks: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-vs-project-tasks`, params }),

  expenseBudgetAnalysis: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/expense-budget-analysis`, params }),

  contractorCostPerformance: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/contractor-cost-performance`, params }),

  budgetVarianceAnalysis: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-variance-analysis`, params }),

  forecastCostAtCompletion: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/forecast-cost-at-completion`, params }),

  budgetControlAlerts: (params?: DashboardParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${DASHBOARD_BASE}/budget-control-alerts`, params }),

  // ==================== REPORT APIs (35) ====================

  // --- Report 1: Project Budget vs Actual (6 endpoints) ---
  projectBudgetVsActualSummary: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/project-budget-vs-actual-summary`, params }),

  projectBudgetVsActualByLine: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/project-budget-vs-actual-by-line`, params }),

  budgetLineTransactions: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-line-transactions`, params }),

  poInvoiceGrnDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/po-invoice-grn-details`, params }),

  woRaBillDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/wo-ra-bill-details`, params }),

  expenseVoucherDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/expense-voucher-details`, params }),

  // --- Report 2: Budget Consumption by Category (6 endpoints) ---
  budgetConsumptionByCategorySummary: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-consumption-by-category-summary`, params }),

  budgetConsumptionByCategoryByLine: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-consumption-by-category-by-line`, params }),

  budgetConsumptionLineTransactions: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-consumption-line-transactions`, params }),

  budgetConsumptionPoInvoiceGrnDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-consumption-po-invoice-grn-details`, params }),

  budgetConsumptionWoRaBillDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-consumption-wo-ra-bill-details`, params }),

  budgetConsumptionExpenseVoucherDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-consumption-expense-voucher-details`, params }),

  // --- Report 3: Budget Consumption by Contractor (3 endpoints) ---
  contractorCostSummary: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/contractor-cost-summary`, params }),

  contractorWorkOrderDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/contractor-work-order-details`, params }),

  contractorWoRaBillDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/contractor-wo-ra-bill-details`, params }),

  // --- Report 4: Budget vs Purchase Orders (3 endpoints) ---
  budgetVsPurchaseOrdersSummary: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-vs-purchase-orders-summary`, params }),

  budgetVsPurchaseOrdersByPr: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-vs-purchase-orders-by-pr`, params }),

  budgetVsPurchaseOrdersPrDrilldown: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-vs-purchase-orders-pr-drilldown`, params }),

  // --- Report 5: Budget vs Expenses (3 endpoints) ---
  budgetVsExpensesSummary: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-vs-expenses-summary`, params }),

  budgetVsExpensesByCategory: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-vs-expenses-by-category`, params }),

  budgetVsExpensesVoucherDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-vs-expenses-voucher-details`, params }),

  // --- Report 6: Budget vs Work Orders (3 endpoints) ---
  budgetVsWorkOrdersSummary: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-vs-work-orders-summary`, params }),

  budgetVsWorkOrdersByWo: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-vs-work-orders-by-wo`, params }),

  budgetVsWorkOrdersRaBillDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-vs-work-orders-ra-bill-details`, params }),

  // --- Report 7: Budget Utilization Trend (5 endpoints) ---
  budgetUtilizationTrend: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-utilization-trend`, params }),

  budgetUtilizationTrendMonthDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-utilization-trend-month-details`, params }),

  budgetUtilizationPoInvoiceGrnDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-utilization-po-invoice-grn-details`, params }),

  budgetUtilizationWoRaBillDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-utilization-wo-ra-bill-details`, params }),

  budgetUtilizationExpenseVoucherDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-utilization-expense-voucher-details`, params }),

  // --- Report 8: Budget Forecast at Completion (6 endpoints) ---
  budgetForecastSummary: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-forecast-summary`, params }),

  budgetForecastByLine: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-forecast-by-line`, params }),

  budgetForecastLineTransactions: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-forecast-line-transactions`, params }),

  budgetForecastPoInvoiceGrnDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-forecast-po-invoice-grn-details`, params }),

  budgetForecastWoRaBillDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-forecast-wo-ra-bill-details`, params }),

  budgetForecastExpenseVoucherDetails: (params?: ReportParams): Promise<any> =>
    makeApiCall({ method: "get", url: `${REPORT_BASE}/budget-forecast-expense-voucher-details`, params }),
};

export default budgetAnalysisDashboardApis;
